import { css } from '@emotion/react';
import { Spin, Button } from 'antd';
import React, { type CSSProperties } from 'react';
import { type Env0Theme } from 'constants/themes.constants';
import styled from 'types/theme.types';
import { Link, type LinkProps } from 'react-router';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';

export type ButtonSize = 'small' | 'large';
export type ButtonType = 'primary' | 'default' | 'danger' | 'link';

export interface CustomButtonProps {
  id?: string;
  type?: ButtonType;
  shape?: 'round' | 'circle';
  size?: ButtonSize;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  href?: string;
  isExternalLink?: boolean;
  isLoading?: boolean;
  addPlus?: boolean;
  block?: boolean;
  flatBottom?: boolean;
  style?: CSSProperties;
  className?: string;
  'data-e2e'?: string;
  'aria-label'?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const CustomButton = ({
  type = 'default',
  onClick,
  children,
  href,
  isExternalLink,
  isLoading = false,
  addPlus = false,
  ...properties
}: CustomButtonProps) => {
  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!href) event.preventDefault();
    onClick?.(event);
  };

  const loaderColor =
    type === 'default' || type === 'link' ? 'var(--icon-primary-standalone)' : 'var(--text-on-primary)';
  const loader = <LoadingOutlined style={{ fontSize: 18, color: loaderColor }} spin />;

  // Figma's Tertiary is antd's `text`, not its `link` (a hyperlink).
  const buttonType = type === 'link' ? 'text' : type;

  // Content stays in the layout at opacity 0 with the spinner overlaid, so the button holds its width
  // without a min-width. Opacity, not visibility or unmounting: those drop the label out of the
  // accessibility tree while the button is busy.
  const styledButton = (
    <StyledButton
      onClick={onClickHandler}
      type={buttonType}
      role={href ? 'link' : 'button'}
      aria-busy={isLoading || undefined}
      data-loading={isLoading || undefined}
      {...properties}>
      {addPlus && <PlusOutlined />}
      {children}
      {isLoading && (
        <span data-loading-indicator>
          <Spin size="small" indicator={loader} />
        </span>
      )}
    </StyledButton>
  );

  if (href) {
    return isExternalLink ? (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
        {styledButton}
      </a>
    ) : (
      <StyledLink
        to={href}
        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => e.stopPropagation()}
        block={properties.block}>
        {styledButton}
      </StyledLink>
    );
  } else {
    return styledButton;
  }
};

export const StyledLink = styled(({ block, ...props }: LinkProps & { block?: boolean }) => <Link {...props} />)`
  border-radius: 4px;
  ${({ block }) => (block ? 'width: 100%;' : '')}
`;

// Not in the bridge: 5.6.4 derives a default button's hover from colorPrimaryHover, which the primary
// button's hover fill also needs at a different value, and has no defaultHoverBg at all.
const secondaryHoverCss = css`
  background-color: var(--surface-secondary-action-hover);
  border-color: var(--surface-secondary-border-hover);
  /* --text-heading, not --text-on-primary: the latter is black in both modes for the lime fill, and
     this hover fill is dark olive in dark mode. Same value in light. */
  color: var(--text-heading);
`;

// `text` is internal: the public prop stays `link`, and CustomButton maps it.
type ButtonExtendedType = Omit<CustomButtonProps, 'type'> & { role: string; type?: ButtonType | 'text' };

// Emotion passes the theme in with the props; the component's own type does not declare it.
type StyledButtonProps = ButtonExtendedType & { theme: Env0Theme };

const StyledButton = styled<React.ComponentType<ButtonExtendedType>>(
  ({ type, ...props }) => (
    <Button type={type === 'danger' ? 'primary' : type} danger={type === 'danger'} {...(props as any)} />
  ),
  {
    shouldForwardProp: propName => propName !== 'flatBottom'
  }
)`
  ${(props: StyledButtonProps) => css`
    /* No emotion focus ring: antd's genFocusStyle draws it for every button, and the theme pins
           its pair (lineWidthFocus + colorPrimaryBorder in antd.constants) to the same ring the text
           fields use — one focus system app-wide (UI-polish review, item B). The old 1px emotion ring
           lost the cascade to antd's and never rendered. */

    /* antd 5.6.4 spaces an icon from its label with the selector "> .anticon + span", which only
           fires when the label is a span sibling. A button whose child is a bare text node never
           matches it, so icon and label collide - which is why several call sites hand-rolled a gap.

           Only display needs the doubled class: antd declares inline-block on .ant-btn behind a
           zero-specificity :where(), which a single class only ties. It declares no justify-content,
           align-items or gap, so those stay at ordinary specificity where a call site still wins by
           source order - command-card, analysis-feedback and two tables rely on that. */
    && {
      display: inline-flex;
    }
    align-items: center;
    justify-content: center;
    gap: ${props.theme.spacing.xs};

    /* Loading holds the width: content keeps its box at opacity 0, spinner overlaid. */
    &[data-loading] {
      position: relative;
      cursor: default;

      > *:not([data-loading-indicator]) {
        opacity: 0;
      }

      [data-loading-indicator] {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    /* Bare text children (every FormattedMessage label) are not elements, so opacity cannot reach them
       and only the colour hides them. It has to outrank every rule that colours a button: antd's hover
       and active (0,4,0) and this wrapper's own type and state colours (up to 0,5,0). Five classes and
       the attribute make 0,6,0. */
    &&&&&[data-loading] {
      color: transparent;
    }

    /* Scoping through antd's own class beats its selector without !important. */
    &&.ant-btn-default:not(:disabled):hover {
      ${secondaryHoverCss}
    }

    /* Figma specs no Pressed state; matching Hovered is the extrapolation (D1). */
    &&.ant-btn-default:not(:disabled):active {
      ${secondaryHoverCss}
    }

    /* Only the button's own leading icon — a direct .anticon child (addPlus) or antd's icon slot.
           A blanket "&& svg" also blew the dropdown chevron up to 16px beside 14px text (UI-polish
           review, "+ Add Variable"). */
    && > .anticon svg,
    && > .ant-btn-icon svg {
      font-size: 16px;
    }

    /* antd hardcodes small's horizontal padding to 8, which is not configurable through the theme
           and left "Run Now" hugging its label (UI-polish review, item K). */
    &&.ant-btn-sm {
      padding-inline: ${props.theme.padding.md};
    }

    /* antd draws a disabled button's border from colorBorder — the lime border.secondary — which
           put a brand-coloured edge on a state that should recede (UI-polish review, item J). */
    &&:disabled {
      border-color: var(--border-disabled);
    }

    ${
      props.type === 'text' &&
      css`
        padding-inline: ${props.theme.padding.md};

        /* Through antd's own class, as on Secondary: antd colours a hovered or pressed text button at
           0,4,0, which a bare :hover (0,3,0) lost to. Pressed matches Hovered (D1). */
        &&.ant-btn-text:not(:disabled):hover,
        &&.ant-btn-text:not(:disabled):active {
          background-color: var(--surface-secondary-action-hover);
          color: var(--text-heading);
        }

        /* Popovers and drawers are that same grey in light mode, so there the fill takes the next step,
           as filter-bar's options do. */
        .ant-popover &&.ant-btn-text:not(:disabled):hover,
        .ant-popover &&.ant-btn-text:not(:disabled):active,
        .ant-drawer &&.ant-btn-text:not(:disabled):hover,
        .ant-drawer &&.ant-btn-text:not(:disabled):active {
          background-color: var(--surface-secondary-background);
        }
      `
    }

    ${
      props.type === 'danger' &&
      css`
        /* Text-error, per the destructive actions in the redesign frames (Destroy 267:6858,
               Archive project 254:32132): no fill, no border, error label — tint fill on hover. The
               token set has no on-error text (colorTextLightSolid is the on-lime black, ~3:1 on a red
               fill), which rules the solid variant out anyway. Doubled class + antd's own, which
               outranks antd's solid danger at rest (0,2,0). */
        &&.ant-btn-dangerous {
          background-color: transparent;
          border-color: transparent;
          color: var(--text-error);
        }

        /* Pressed matches Hovered (D1). The label beats antd's hover and active label (0,4,0) and stays
           below the busy colour (0,6,0); the tint takes a third class, as antd's danger fills are 0,5,0. */
        &&.ant-btn-dangerous:not(:disabled):hover,
        &&.ant-btn-dangerous:not(:disabled):active {
          color: var(--text-error);
        }

        &&&.ant-btn-dangerous:not(:disabled):hover,
        &&&.ant-btn-dangerous:not(:disabled):active {
          background-color: var(--surface-error-background);
          border-color: transparent;
        }

        /* Greyed like every other disabled button: the error label above tied antd's disabled grey
           (0,3,0) and won on source order. */
        &&.ant-btn-dangerous:disabled {
          background-color: transparent;
          color: var(--text-disabled);
        }
      `
    }
  `}

  ${(props: ButtonExtendedType) =>
    props.flatBottom &&
    `
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  `}
`;

/**
 * A Fix
 * Mouse events don't trigger at disabled button in Chrome
 *
 * This fixes the scenario when we wrap a disabled button with a Tooltip.
 * Without this fix the Tooltip message will not disappear "onmouseleave"
 *
 * relevant snippet: https://github.com/ant-design/ant-design/pull/4865/files#diff-186839a30bf8b9d67a4b10bf7c091d5f
 */
CustomButton.__ANT_BUTTON = true;

export default CustomButton;
